import { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle2, Building2, Users2, DollarSign, Calendar, Clock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import { eventTypes, referralOptions, PHOTO_LEADERSHIP, PHOTO_CEILI, VENUE_COLLAGE } from '../mock';
import { useI18n } from '../contexts/I18nContext';
import HoneypotField from '../components/HoneypotField';

const initial = {
  name: '',
  organization: '',
  email: '',
  phone: '',
  eventType: '',
  guests: '',
  preferredDates: '',
  startTime: '',
  endTime: '',
  tablesChairs: 'yes',
  catering: 'no',
  specialRequests: '',
  referral: '',
};

export default function RentalsPage() {
  const { t } = useI18n();
  const [form, setForm] = useState(initial);
  const [hp, setHp] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.eventType) {
      toast.error('Please fill in name, email, and event type.');
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/forms/submit`, {
        form_type: 'rental',
        source_page: 'rentals',
        data: { ...form, website: hp },
      });
    } catch (err) { /* silent fail */ }
    toast.success(`Thanks ${form.name}!`);
    setSubmitted(true);
    setForm(initial);
  };

  return (
    <PageShell
      eyebrow={t('rentals.eyebrow')}
      title={<>{t('rentals.title1')} <span className="italic text-[#E5614C]">{t('rentals.title2')}</span></>}
      subtitle={t('rentals.subtitle')}
      accent="coral"
    >
      <SEO
        title="Rent Our Space — Framingham Venue"
        description="Host your next event at GEMS @ The Arts, a flexible Framingham, MA venue perfect for weddings, performances, workshops, meetings, and community gatherings. Affordable rates, nonprofit-friendly, full A/V."
        path="/rentals"
      />
      {/* Image gallery - using real venue photos */}
      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid grid-cols-3 gap-3 sm:gap-4">
          <div className="col-span-2 row-span-2 h-[260px] sm:h-[380px] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)]">
            <img src={PHOTO_LEADERSHIP} alt="Women in Nonprofit Leadership event at GEMS @ The Arts" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
          </div>
          <div className="h-[125px] sm:h-[182px] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)]">
            <img src={PHOTO_CEILI} alt="Irish Ceili dance event" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
          </div>
          <div className="h-[125px] sm:h-[182px] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)]">
            <img src={VENUE_COLLAGE} alt="Collage of the GEMS @ The Arts venue and community murals" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
          </div>
        </div>
      </section>

      {/* Details / perks */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: DollarSign, title: t('rentals.perk1.title'), desc: t('rentals.perk1.desc') },
              { icon: Users2, title: t('rentals.perk2.title'), desc: t('rentals.perk2.desc') },
              { icon: Building2, title: t('rentals.perk3.title'), desc: t('rentals.perk3.desc') },
            ].map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 border border-[#2A1B1A]/8 hover:border-[#E5614C]/30 hover:-translate-y-1 hover:shadow-[0_24px_50px_-25px_rgba(229,97,76,0.35)] transition-[transform,box-shadow,border-color] duration-300"
              >
                <div className="h-12 w-12 rounded-xl bg-[#E5614C]/10 text-[#E5614C] flex items-center justify-center">
                  <p.icon className="h-6 w-6" />
                </div>
                <h3 className="font-serif-display font-bold text-[22px] text-[#2A1B1A] mt-5">{p.title}</h3>
                <p className="text-[14px] text-[#2A1B1A]/70 mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-[#2A1B1A]/75 text-[17px]">
            <span className="font-semibold text-[#E5614C]">{t('rentals.inviteCta')}</span>
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="inquiry" className="pb-24">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="bg-white rounded-[28px] p-7 lg:p-12 shadow-[0_40px_80px_-30px_rgba(42,27,26,0.25)] border border-[#2A1B1A]/5">
            <h2 className="font-serif-display text-[34px] lg:text-[42px] font-bold text-[#2A1B1A]">{t('rentals.form.title')}</h2>
            <p className="text-[15px] text-[#2A1B1A]/65 mt-2">{t('rentals.form.replyNote')}</p>

            {submitted ? (
              <div className="mt-8 rounded-2xl bg-[#5DCAB5]/15 border border-[#5DCAB5]/30 p-6 flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-[#5DCAB5] mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-[#2A1B1A]">{t('rentals.form.success')}</div>
                  <p className="text-[14px] text-[#2A1B1A]/70 mt-1">{t('rentals.form.successDesc')}{' '}
                    <button onClick={() => setSubmitted(false)} className="text-[#E5614C] font-semibold underline">{t('rentals.form.another')}</button>
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5 relative">
                <HoneypotField value={hp} onChange={setHp} />
                <FormField label={t('rentals.form.name')}>
                  <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder={t('rentals.form.namePh')} className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                </FormField>
                <FormField label={t('rentals.form.org')}>
                  <Input value={form.organization} onChange={(e) => update('organization', e.target.value)} placeholder={t('rentals.form.orgPh')} className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                </FormField>
                <FormField label={t('rentals.form.email')}>
                  <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder={t('rentals.form.emailPh')} className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                </FormField>
                <FormField label={t('rentals.form.phone')}>
                  <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder={t('rentals.form.phonePh')} className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                </FormField>
                <FormField label={t('rentals.form.eventType')}>
                  <Select value={form.eventType} onValueChange={(v) => update('eventType', v)}>
                    <SelectTrigger className="h-12 rounded-xl border-[#2A1B1A]/15 focus:ring-[#E5614C]/40"><SelectValue placeholder={t('rentals.form.eventTypePh')} /></SelectTrigger>
                    <SelectContent>{eventTypes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </FormField>
                <FormField label={t('rentals.form.guests')}>
                  <Input type="number" min="0" value={form.guests} onChange={(e) => update('guests', e.target.value)} placeholder={t('rentals.form.guestsPh')} className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                </FormField>
                <FormField label={t('rentals.form.dates')} full>
                  <div className="relative">
                    <Calendar className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#2A1B1A]/50" />
                    <Input value={form.preferredDates} onChange={(e) => update('preferredDates', e.target.value)} placeholder={t('rentals.form.datesPh')} className="h-12 pl-11 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                  </div>
                </FormField>
                <FormField label={t('rentals.form.start')}>
                  <div className="relative">
                    <Clock className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#2A1B1A]/50" />
                    <Input value={form.startTime} onChange={(e) => update('startTime', e.target.value)} placeholder={t('rentals.form.startPh')} className="h-12 pl-11 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                  </div>
                </FormField>
                <FormField label={t('rentals.form.end')}>
                  <div className="relative">
                    <Clock className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#2A1B1A]/50" />
                    <Input value={form.endTime} onChange={(e) => update('endTime', e.target.value)} placeholder={t('rentals.form.endPh')} className="h-12 pl-11 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
                  </div>
                </FormField>
                <FormField label={t('rentals.form.tables')} full>
                  <RadioRow
                    name="tablesChairs"
                    value={form.tablesChairs}
                    onChange={(v) => update('tablesChairs', v)}
                    options={[
                      { v: 'yes', l: t('rentals.form.tables.yes') },
                      { v: 'some', l: t('rentals.form.tables.some') },
                      { v: 'no', l: t('rentals.form.tables.no') },
                    ]}
                  />
                </FormField>
                <FormField label={t('rentals.form.food')} full>
                  <RadioRow
                    name="catering"
                    value={form.catering}
                    onChange={(v) => update('catering', v)}
                    options={[
                      { v: 'no', l: t('rentals.form.food.no') },
                      { v: 'self', l: t('rentals.form.food.self') },
                      { v: 'catered', l: t('rentals.form.food.catered') },
                    ]}
                  />
                </FormField>
                <FormField label={t('rentals.form.referral')} full>
                  <Select value={form.referral} onValueChange={(v) => update('referral', v)}>
                    <SelectTrigger className="h-12 rounded-xl border-[#2A1B1A]/15 focus:ring-[#E5614C]/40"><SelectValue placeholder={t('rentals.form.referralPh')} /></SelectTrigger>
                    <SelectContent>{referralOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </FormField>
                <FormField label={t('rentals.form.special')} full>
                  <Textarea rows={4} value={form.specialRequests} onChange={(e) => update('specialRequests', e.target.value)} placeholder={t('rentals.form.specialPh')} className="rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C] resize-none" />
                </FormField>
                <div className="sm:col-span-2">
                  <Button type="submit" className="w-full h-13 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold py-3.5 btn-press text-[16px]">
                    {t('rentals.form.submit')} <Send className="h-4 w-4 ml-2" />
                  </Button>
                  <p className="text-[12px] text-[#2A1B1A]/50 text-center mt-3 flex items-center justify-center gap-1.5">
                    <MapPin className="h-3 w-3" /> 160 Hollis Street, Framingham, MA · info@gemsatthearts.org
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function FormField({ label, full, children }) {
  return (
    <div className={`space-y-1.5 ${full ? 'sm:col-span-2' : ''}`}>
      <Label className="text-[#2A1B1A] text-[13px] font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function RadioRow({ name, value, onChange, options }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-3">
      {options.map((o) => (
        <label
          key={o.v}
          htmlFor={`${name}-${o.v}`}
          className={`flex items-center gap-2 px-4 h-11 rounded-full border cursor-pointer transition-colors ${
            value === o.v
              ? 'bg-[#E5614C] border-[#E5614C] text-white'
              : 'bg-white border-[#2A1B1A]/15 text-[#2A1B1A] hover:border-[#E5614C]'
          }`}
        >
          <RadioGroupItem id={`${name}-${o.v}`} value={o.v} className="sr-only" />
          <span className="text-[14px] font-medium">{o.l}</span>
        </label>
      ))}
    </RadioGroup>
  );
}
